<template>
  <div class="congruence-similarity-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex> \$2\$ </div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg viewBox="-200 -200 400 360" class="canvas-svg">
          <!-- Triangle ABC -->
          <polygon :points="`${ptA.x},${ptA.y} ${ptB.x},${ptB.y} ${ptC.x},${ptC.y}`" fill="rgba(13, 104, 255, 0.1)" stroke="#0D68FF" stroke-width="2" />
          <text :x="ptA.x - 10" :y="ptA.y - 10" fill="#8ba1ff" font-weight="bold">A</text>
          <text :x="ptB.x - 15" :y="ptB.y + 15" fill="#8ba1ff" font-weight="bold">B</text>
          <text :x="ptC.x + 10" :y="ptC.y + 15" fill="#8ba1ff" font-weight="bold">C</text>

          <!-- Extensions if O is outside -->
          <g stroke="rgba(255,255,255,0.1)" stroke-width="1.5" stroke-dasharray="3,3">
             <line :x1="ptA.x" :y1="ptA.y" :x2="footD.x" :y2="footD.y" />
             <line :x1="ptB.x" :y1="ptB.y" :x2="footE.x" :y2="footE.y" />
             <line :x1="ptC.x" :y1="ptC.y" :x2="footF.x" :y2="footF.y" />
             
             <!-- Extend triangle sides -->
             <line :x1="ptB.x - (ptC.x-ptB.x)" :y1="ptB.y - (ptC.y-ptB.y)" :x2="ptC.x + (ptC.x-ptB.x)" :y2="ptC.y + (ptC.y-ptB.y)" />
             <line :x1="ptC.x - (ptA.x-ptC.x)" :y1="ptC.y - (ptA.y-ptC.y)" :x2="ptA.x + (ptA.x-ptC.x)" :y2="ptA.y + (ptA.y-ptC.y)" />
             <line :x1="ptA.x - (ptB.x-ptA.x)" :y1="ptA.y - (ptB.y-ptA.y)" :x2="ptB.x + (ptB.x-ptA.x)" :y2="ptB.y + (ptB.y-ptA.y)" />
          </g>

          <!-- Ceva Lines -->
          <line :x1="ptA.x" :y1="ptA.y" :x2="ptO.x" :y2="ptO.y" stroke="#2AF598" stroke-width="2" />
          <line :x1="ptB.x" :y1="ptB.y" :x2="ptO.x" :y2="ptO.y" stroke="#2AF598" stroke-width="2" />
          <line :x1="ptC.x" :y1="ptC.y" :x2="ptO.x" :y2="ptO.y" stroke="#2AF598" stroke-width="2" />
          <line :x1="ptO.x" :y1="ptO.y" :x2="footD.x" :y2="footD.y" stroke="#2AF598" stroke-dasharray="2,2" />
          <line :x1="ptO.x" :y1="ptO.y" :x2="footE.x" :y2="footE.y" stroke="#2AF598" stroke-dasharray="2,2" />
          <line :x1="ptO.x" :y1="ptO.y" :x2="footF.x" :y2="footF.y" stroke="#2AF598" stroke-dasharray="2,2" />

          <!-- Intersection Point O -->
          <circle :cx="ptO.x" :cy="ptO.y" r="5" fill="#facc15" />
          <text :x="ptO.x + 8" :y="ptO.y - 8" fill="#facc15" font-weight="bold">O</text>

          <!-- Feet Points D, E, F -->
          <circle :cx="footD.x" :cy="footD.y" r="4" fill="#f43f5e" />
          <circle :cx="footE.x" :cy="footE.y" r="4" fill="#f43f5e" />
          <circle :cx="footF.x" :cy="footF.y" r="4" fill="#f43f5e" />
          
          <text :x="footD.x" :y="footD.y + 15" fill="#f43f5e" font-size="14" font-weight="bold">D</text>
          <text :x="footE.x+10" :y="footE.y - 5" fill="#f43f5e" font-size="14" font-weight="bold">E</text>
          <text :x="footF.x-15" :y="footF.y - 5" fill="#f43f5e" font-size="14" font-weight="bold">F</text>
        </svg>

        <div class="hud-center">
          <div class="status-badge con">
             三线共点 O ⇔ 塞瓦比值为 1
          </div>
        </div>
      </div>

      <div class="compact-controls">
         <div class="control-card glass">
            <div class="control-vertical-group">
               <div class="param-row">
                  <div class="s-item" style="grid-column: span 2;">
                     <label>游动交点 O 的水平位置 (X)</label>
                     <el-slider v-model="ptO_x" :min="-150" :max="150" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item" style="grid-column: span 2;">
                     <label>游动交点 O 的垂直位置 (Y)</label>
                     <el-slider v-model="ptO_y" :min="-100" :max="120" input-size="mini"></el-slider>
                  </div>
               </div>
            </div>
         </div>
         <div class="discovery-card-compact" v-latex>
            <i>🔍</i>
            <p><strong>塞瓦定理：</strong>在 $\triangle ABC$ 内外任取一点 $O$（不与顶点重合），连接 $AO, BO, CO$ 并延长交对边于 $D, E, F$。则分成的六条短线段必然满足交比倒数连乘等于 $1$。这是研究“多线共点”最核心的工具之一！</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'CevaTheoremViz',
  data() {
    return {
      ptA: { x: 0, y: -120 },
      ptB: { x: -120, y: 80 },
      ptC: { x: 140, y: 100 },
      ptO_x: -20,
      ptO_y: 20
    }
  },
  computed: {
    ptO() { return { x: this.ptO_x, y: this.ptO_y }; },
    footD() { return this.intersect(this.ptA, this.ptO, this.ptB, this.ptC); },
    footE() { return this.intersect(this.ptB, this.ptO, this.ptC, this.ptA); },
    footF() { return this.intersect(this.ptC, this.ptO, this.ptA, this.ptB); }
  },
  methods: {
    intersect(p1, p2, p3, p4) {
      let x1=p1.x, y1=p1.y, x2=p2.x, y2=p2.y;
      let x3=p3.x, y3=p3.y, x4=p4.x, y4=p4.y;
      
      let denom = (x1-x2)*(y3-y4) - (y1-y2)*(x3-x4);
      if (Math.abs(denom) < 0.0001) return {x: 9999, y: 9999}; 

      let numX = (x1*y2 - y1*x2)*(x3-x4) - (x1-x2)*(x3*y4 - y3*x4);
      let numY = (x1*y2 - y1*x2)*(y3-y4) - (y1-y2)*(x3*y4 - y3*x4);
      return { x: numX/denom, y: numY/denom };
    }
  }
}
</script>

<style scoped lang="scss">
.congruence-similarity-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #10b981; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.main-layout { display: flex; gap: 12px; height: 100%; min-height: 0; flex-direction: column; }
.simulation-stage { flex: 1; min-height: 200px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .canvas-svg { width: 100%; height: 100%; } .hud-center { position: absolute; top: 15px; width: 100%; text-align: center; display: flex; justify-content: center; .status-badge { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); padding: 4px 16px; border-radius: 20px; color: #fff; font-weight: bold; font-size: 13px; backdrop-filter: blur(4px); &.con { background: rgba(16,185,129,0.2); border-color: #10b981; color: #6ee7b7; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .param-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; display: block; margin-bottom: -5px; } } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { font-style: normal; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
